import * as turf from '@turf/turf';

import { GeoLocation } from '@features/components/visualization/mapbox/models/map-data';

/** A GeoJSON position: longitude first, then latitude. */
export type Position = [number, number];
export type Ring = Position[];

export interface GeoJsonPolygon {
  type: 'Polygon';
  coordinates: Ring[];
}

export type PolygonProblem = 'empty' | 'too-few-vertices' | 'self-intersecting';

export function closeRing(ring: Ring): Ring {
  if (ring.length === 0) {
    return ring;
  }
  const [firstLng, firstLat] = ring[0];
  const [lastLng, lastLat] = ring[ring.length - 1];
  return firstLng === lastLng && firstLat === lastLat
    ? ring
    : [...ring, [firstLng, firstLat]];
}

export function distinctVertexCount(ring: Ring): number {
  return new Set(ring.map(([lng, lat]) => `${lng},${lat}`)).size;
}

/**
 * The ring without the vertex at `index`, or null when the removal is refused: either the
 * index addresses no vertex, or what would remain is no longer a polygon. `index` counts
 * the non-closing positions, matching the `coord_path` mapbox-gl-draw puts on a vertex.
 */
export function removeRingVertex(ring: Ring, index: number): Ring | null {
  // The closing position repeats the first, so it is not addressable and follows it.
  const addressable = closeRing(ring).slice(0, -1);
  if (index < 0 || index >= addressable.length) {
    return null;
  }

  const remaining = addressable.filter((_, position) => position !== index);
  if (distinctVertexCount(remaining) < 3) {
    return null;
  }
  return closeRing(remaining);
}

/** Shoelace area. Positive means counter-clockwise. */
export function signedArea(ring: Ring): number {
  const closed = closeRing(ring);
  let total = 0;
  for (let i = 0; i < closed.length - 1; i++) {
    const [x1, y1] = closed[i];
    const [x2, y2] = closed[i + 1];
    total += x1 * y2 - x2 * y1;
  }
  return total / 2;
}

export function normalizeWinding(ring: Ring): Ring {
  const closed = closeRing(ring);
  return signedArea(closed) < 0 ? [...closed].reverse() : closed;
}

function isPosition(value: unknown): value is Position {
  return (
    Array.isArray(value) && typeof value[0] === 'number' && typeof value[1] === 'number'
  );
}

/**
 * The rings that are actually shaped like rings. `geometry` is an unvalidated
 * `serde_json::Value` on the backend and can also be hand-written in the JSON editor, so a
 * truncated or mis-nested payload (`coordinates` absent, or a bare ring where an array of
 * rings belongs) must degrade to "no geometry" instead of throwing out of a computed.
 */
function coordinateRings(polygon: GeoJsonPolygon | null | undefined): Ring[] {
  const coordinates = polygon?.coordinates;
  if (!Array.isArray(coordinates)) {
    return [];
  }
  return coordinates.filter(
    (ring): ring is Ring => Array.isArray(ring) && ring.every(isPosition)
  );
}

export function outerRing(polygon: GeoJsonPolygon): Ring {
  return coordinateRings(polygon)[0] ?? [];
}

export function holeRings(polygon: GeoJsonPolygon): Ring[] {
  return coordinateRings(polygon).slice(1);
}

export function hasHoles(polygon: GeoJsonPolygon): boolean {
  return coordinateRings(polygon).length > 1;
}

/**
 * Interior rings are closed but never rewound: they are carried through untouched so a
 * geometry authored elsewhere survives a round trip.
 */
export function makePolygon(outer: Ring, holes: Ring[] = []): GeoJsonPolygon {
  return {
    type: 'Polygon',
    coordinates: [normalizeWinding(outer), ...holes.map(closeRing)]
  };
}

/**
 * turf rejects rings shorter than four positions. Only the exterior ring disqualifies the
 * polygon: a degenerate interior ring is dropped by `toTurfPolygon` instead, so an imported
 * geometry with a junk hole still reports its real area and its real self-intersections.
 */
export function isDrawablePolygon(polygon: GeoJsonPolygon): boolean {
  return closeRing(outerRing(polygon)).length >= 4;
}

function toTurfPolygon(polygon: GeoJsonPolygon) {
  const holes = holeRings(polygon)
    .map(closeRing)
    .filter((ring) => ring.length >= 4);
  return turf.polygon([closeRing(outerRing(polygon)), ...holes]);
}

export function isSelfIntersecting(polygon: GeoJsonPolygon): boolean {
  if (!isDrawablePolygon(polygon)) {
    return false;
  }
  return turf.kinks(toTurfPolygon(polygon)).features.length > 0;
}

export function validatePolygon(polygon: GeoJsonPolygon | null): PolygonProblem | null {
  if (!polygon || outerRing(polygon).length === 0) {
    return 'empty';
  }
  if (distinctVertexCount(outerRing(polygon)) < 3) {
    return 'too-few-vertices';
  }
  if (isSelfIntersecting(polygon)) {
    return 'self-intersecting';
  }
  return null;
}

export function polygonAreaKm2(polygon: GeoJsonPolygon): number {
  if (!isDrawablePolygon(polygon)) {
    return 0;
  }
  return turf.area(toTurfPolygon(polygon)) / 1_000_000;
}

export function countPointsInside(points: GeoLocation[], polygon: GeoJsonPolygon): number {
  if (!isDrawablePolygon(polygon)) {
    return 0;
  }
  const target = toTurfPolygon(polygon);
  return points.filter((point) =>
    turf.booleanPointInPolygon(turf.point([point.lng, point.lat]), target)
  ).length;
}

export function ringToGeoLocations(ring: Ring): GeoLocation[] {
  return ring.map(([lng, lat]) => ({ lat, lng }));
}

export function geoLocationsToRing(locations: GeoLocation[]): Ring {
  return locations.map((location) => [location.lng, location.lat]);
}

export function ringToSvgPath(
  ring: Ring,
  width: number,
  height: number,
  padding = 2
): string {
  if (ring.length === 0) {
    return '';
  }

  const lngs = ring.map(([lng]) => lng);
  const lats = ring.map(([, lat]) => lat);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);

  const spanLng = maxLng - minLng || 1;
  const spanLat = maxLat - minLat || 1;
  const innerWidth = Math.max(width - padding * 2, 0);
  const innerHeight = Math.max(height - padding * 2, 0);
  const scale = Math.min(innerWidth / spanLng, innerHeight / spanLat);
  const offsetX = padding + (innerWidth - spanLng * scale) / 2;
  const offsetY = padding + (innerHeight - spanLat * scale) / 2;

  const round = (value: number) => Math.round(value * 100) / 100;
  const points = ring.map(([lng, lat]) => {
    const x = offsetX + (lng - minLng) * scale;
    // SVG's y axis grows downward while latitude grows upward.
    const y = offsetY + (maxLat - lat) * scale;
    return `${round(x)},${round(y)}`;
  });

  return `M${points.join('L')}Z`;
}
